import React, { useMemo, useState, Children } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "../context/TranslationContext";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { FileText, Plus, ArrowRight, Clock, CheckCircle2, Circle, Globe2, RefreshCw, AlertCircle, Loader2, Search, X, Key, Book, LogOut, ArrowLeft } from "lucide-react";
import { NewDocumentModal } from "../components/NewDocumentModal";
import { GlossaryDrawer } from "../components/GlossaryDrawer";
import { DEFAULT_MODELS } from "../data/translationService";
export const DocumentsList: React.FC = () => {
  const {
    documents,
    isLoading,
    error,
    refreshDocuments,
    openRouterApiKey
  } = useTranslation();
  const {
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);
  // Show only FR locale documents (both active and inactive)
  const filteredDocuments = useMemo(() => {
    let docs = documents;
    // Only show documents that have FR locale
    docs = docs.filter(doc => doc.locales?.includes("FR"));
    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      docs = docs.filter(doc => doc.title.toLowerCase().includes(q));
    }
    return docs.map(doc => ({
      doc,
      locale: "FR" as const,
      rowKey: `${doc.id}-FR`
    }));
  }, [documents, searchQuery]);
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full border border-emerald-200/50" data-id="element-244">
            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-245" /> Completed
          </span>;
      case "translating":
        return <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 text-slate-500 text-xs font-medium rounded-full border border-slate-200/50" data-id="element-246">
            <Circle className="w-3.5 h-3.5" data-id="element-247" />
            Not Started
          </span>;
      default:
        return <span className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-200/50" data-id="element-248">
            <Clock className="w-3.5 h-3.5" data-id="element-249" /> In Review
          </span>;
    }
  };
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.02
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 4
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-slate-50 p-8" data-id="element-250">
      <div className="max-w-5xl mx-auto space-y-6" data-id="element-251">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-252">
          <div data-id="element-253">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors mb-3" data-id="element-254">
              <ArrowLeft className="w-4 h-4" data-id="element-255" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-id="element-256">
              <div className="p-2 bg-indigo-600 text-white rounded-lg shadow-sm" data-id="element-257">
                <Globe2 className="w-6 h-6" data-id="element-258" />
              </div>
              Translation Workspace
            </h1>
            <p className="text-slate-500 mt-1" data-id="element-259">
              Manage and translate your legal documents
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-260">
            <button onClick={() => {
            logout();
            navigate("/login");
          }} className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" title="Log out" data-id="element-261">
              <LogOut className="w-4 h-4" data-id="element-262" />
              <span className="hidden sm:inline" data-id="element-263">Log out</span>
            </button>
            <button onClick={() => navigate("/settings")} className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${openRouterApiKey ? "bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100" : "bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100"}`} data-id="element-264">
              <Key className="w-4 h-4" data-id="element-265" />
              {openRouterApiKey ? "Settings ✓" : "Setup Required"}
            </button>
            <button onClick={() => setIsGlossaryOpen(true)} className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-266">
              <Book className="w-4 h-4" data-id="element-267" />
              Glossary
            </button>
            <button onClick={() => refreshDocuments()} disabled={isLoading} className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50" data-id="element-268">
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} data-id="element-269" />
              Refresh
            </button>
            <button onClick={() => setIsModalOpen(true)} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-270">
              <Plus className="w-4 h-4" data-id="element-271" />
              New Document
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative" data-id="element-272">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-273" />
          <input type="text" placeholder="Search documents..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-11 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all shadow-sm" data-id="element-274" />

          {searchQuery && <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors" data-id="element-275">
              <X className="w-4 h-4" data-id="element-276" />
            </button>}
        </div>

        {/* Document Count */}
        {!isLoading && !error && documents.length > 0 && <div className="px-1" data-id="element-277">
            <div className="text-xs text-slate-400 font-medium" data-id="element-278">
              {filteredDocuments.length} FR template
              {filteredDocuments.length !== 1 ? "s" : ""}
              {searchQuery && ` matching "${searchQuery}"`}
            </div>
          </div>}

        {/* Loading State */}
        {isLoading && documents.length === 0 && <div className="flex flex-col items-center justify-center py-24 text-center" data-id="element-279">
            <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mb-4" data-id="element-280" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-281">
              Loading documents...
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-282">
              Fetching templates from the server
            </p>
          </div>}

        {/* Error State */}
        {error && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-4" data-id="element-283">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-284" />
            <div className="flex-1" data-id="element-285">
              <h3 className="font-semibold text-red-900 mb-1" data-id="element-286">
                Failed to load documents
              </h3>
              <p className="text-sm text-red-700 mb-3" data-id="element-287">{error}</p>
              <button onClick={() => refreshDocuments()} className="flex items-center gap-2 px-4 py-2 bg-white border border-red-200 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors" data-id="element-288">
                <RefreshCw className="w-4 h-4" data-id="element-289" />
                Try Again
              </button>
            </div>
          </div>}

        {/* Documents List */}
        {!isLoading && !error && documents.length > 0 && <>
            {filteredDocuments.length > 0 ? <motion.div variants={container} initial="hidden" animate="show" className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100" data-id="element-290">
                {filteredDocuments.map(({
            doc,
            locale,
            rowKey
          }) => {
            const approvedCount = doc.segments.filter(s => s.status === "approved").length;
            const totalCount = doc.segments.length;
            const progress = totalCount > 0 ? Math.round(approvedCount / totalCount * 100) : 0;
            const isPaired = (doc.locales?.length || 0) > 1;
            return <motion.div key={rowKey} variants={item} onClick={() => navigate(`/document/${doc.id}`)} className="flex items-center gap-5 px-5 py-4 hover:bg-slate-50 cursor-pointer transition-colors group" data-id="element-291">
                      {/* Locale badge */}
                      <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm transition-colors ${locale === "FR" ? "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100" : "bg-slate-100 text-slate-500 group-hover:bg-indigo-50 group-hover:text-indigo-600"}`} data-id="element-292">
                        {locale}
                      </div>

                      {/* Title & meta */}
                      <div className="flex-1 min-w-0" data-id="element-293">
                        <div className="flex items-center gap-2" data-id="element-294">
                          <h3 className={`text-sm font-semibold truncate group-hover:text-indigo-600 transition-colors ${doc.isActive ? "text-slate-900" : "text-slate-400"}`} data-id="element-295">
                            {doc.title}
                          </h3>
                          {isPaired && <span className="shrink-0 px-1.5 py-0.5 bg-indigo-50 text-indigo-500 text-[10px] font-medium rounded border border-indigo-100" data-id="element-296">
                              Paired
                            </span>}
                          {!doc.isActive && <span className="shrink-0 px-1.5 py-0.5 bg-slate-100 text-slate-400 text-[10px] font-medium rounded" data-id="element-297">
                              Inactive
                            </span>}
                        </div>
                        <div className="flex items-center gap-3 mt-1" data-id="element-298">
                          {doc.templateId && <span className="text-[10px] font-mono text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-150" data-id="element-299">
                              #{doc.templateId}
                            </span>}
                          <span className="flex items-center gap-1 text-xs text-slate-400" data-id="element-300">
                            <span className="bg-slate-100 px-1 py-0.5 rounded text-[10px] font-medium text-slate-500" data-id="element-301">
                              EN
                            </span>
                            <ArrowRight className="w-2.5 h-2.5" data-id="element-302" />
                            <span className="bg-indigo-50 px-1 py-0.5 rounded text-[10px] font-medium text-indigo-600" data-id="element-303">
                              FR
                            </span>
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-304">
                            {totalCount} segment{totalCount !== 1 ? "s" : ""}
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-305">
                            {new Date(doc.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })}
                          </span>
                        </div>
                      </div>

                      {/* Progress */}
                      <div className="shrink-0 w-32 hidden sm:block" data-id="element-306">
                        <div className="flex justify-between items-end mb-1.5" data-id="element-307">
                          <span className="text-[11px] text-slate-500" data-id="element-308">
                            {approvedCount}/{totalCount}
                          </span>
                          <span className="text-[11px] font-semibold text-slate-600" data-id="element-309">
                            {progress}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden" data-id="element-310">
                          <div className={`h-full rounded-full transition-all duration-700 ${progress === 100 ? "bg-emerald-500" : "bg-indigo-500"}`} style={{
                    width: `${progress}%`
                  }} data-id="element-311" />
                        </div>
                      </div>

                      {/* Status */}
                      <div className="shrink-0 hidden md:block" data-id="element-312">
                        {getStatusBadge(doc.status)}
                      </div>

                      {/* Arrow */}
                      <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-indigo-500" data-id="element-313">
                        <ArrowRight className="w-4 h-4" data-id="element-314" />
                      </div>
                    </motion.div>;
          })}
              </motion.div> : <div className="bg-white rounded-xl border border-slate-200 p-10 text-center" data-id="element-315">
                <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" data-id="element-316" />
                <h3 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-317">
                  No results
                </h3>
                <p className="text-sm text-slate-500" data-id="element-318">
                  No documents match "{searchQuery}"
                </p>
              </div>}
          </>}

        {/* Empty State */}
        {!isLoading && !error && documents.length === 0 && <div className="bg-white rounded-xl border border-slate-200 border-dashed p-12 flex flex-col items-center justify-center text-center" data-id="element-319">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mb-4" data-id="element-320">
              <FileText className="w-8 h-8 text-indigo-600" data-id="element-321" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-322">
              No documents found
            </h3>
            <p className="text-slate-500 max-w-sm mb-6" data-id="element-323">
              No templates were returned from the server. You can paste a
              document manually to get started.
            </p>
            <button onClick={() => setIsModalOpen(true)} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-324">
              <Plus className="w-4 h-4" data-id="element-325" />
              New Document
            </button>
          </div>}
      </div>

      <NewDocumentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-326" />

      <GlossaryDrawer isOpen={isGlossaryOpen} onClose={() => setIsGlossaryOpen(false)} data-id="element-327" />
    </div>;
};